import CardTemplate from "components/card/card-template/CardTemplate";
import React from "react";
import { useQuote } from "hooks/useQuote";
import { useOffer } from "hooks/useOffer";
import CallToActionButton from "components/form-fields/call-to-action-button/CallToActionButton";
import { COMPANY_CONFIG } from "companyConfig";
import { formatPhone } from "utils/formatPhone";
import { useProductContext } from "context/ProductContext";
import { getMLTADescription } from "utils/mltaQuoteUtils";
import { formatRegisteredSymbol } from "utils/textFormatting";
import { formatUSCurrency } from "utils/formatUSCurrency";
import { getGIWLProductUrl } from "utils/giwlUrl";
import { appendSearchToUrl } from "utils/campaignParams";
import { useCampaignSearch } from "hooks/useCampaignSearch";

interface ApprovedDecisionProps {
    productName: string;
    firstName: string;
    selectedCoverageAmount: number;
    selectedCoveragePremium: number;
    policyNumber: string;
    navigateTo?: string;
}

const ApprovedDecision = ({
    productName,
    firstName,
    selectedCoverageAmount,
    selectedCoveragePremium,
    policyNumber,
    navigateTo = "/quote",
}: ApprovedDecisionProps) => {
    // ========== Hooks ==========
    const { quoteData } = useQuote();
    const { offerData } = useOffer();
    const { isDTO, isMLTA, isGIWL } = useProductContext();
    const campaignSearch = useCampaignSearch();

    // ========== Computed Values ==========
    const applicantType = quoteData?.applicantType ?? "member";
    const memberOfferAvailable = offerData?.memberOfferAvailable ?? true;
    const spouseOfferAvailable = offerData?.spouseOfferAvailable ?? true;

    const confirmationText =
        isMLTA || isGIWL ? (
            <>
                In the next few days, you'll receive your official certificate of insurance in the mail—keep it with your
                important records. This certificate includes the full details of your coverage and benefits. If you have
                questions in the meantime, our Member Services team is ready to help at{" "}
                <span className="text-sm font-semibold">
                    <a href={`tel:${COMPANY_CONFIG.number}`}>{formatPhone(COMPANY_CONFIG.number)}</a>
                </span>{" "}
                or{" "}
                <span className="text-sm font-semibold">
                    <a href={`mailto:${COMPANY_CONFIG.email}`}>{COMPANY_CONFIG.email}</a>
                </span>
                .
            </>
        ) : applicantType === "spouse" ? (
            "We will email your spouse with a confirmation of their policy information and a link to setup a policy portal account. This will allow them to manage their policy details online."
        ) : (
            "We will email you with a confirmation of your policy information and a link to setup your policy portal account. This will allow you to manage your policy details online."
        );

    // For MLTA, show coverage tier and type instead of amount (e.g., "Superior Family Coverage").
    // GIWL and DTO both show the coverage amount as currency.
    const mltaDescription = getMLTADescription(quoteData?.selectedTier, quoteData?.selectedCoverageType);

    // CTA destinations carry the landed campaign params so attribution survives
    // into the follow-up application.
    const giwlSpouseHref = appendSearchToUrl(getGIWLProductUrl(), campaignSearch);
    const dtoNavigateTo = appendSearchToUrl(navigateTo, campaignSearch);

    const formattedCoverageAmount = isMLTA
        ? mltaDescription
        : selectedCoverageAmount
        ? formatUSCurrency(selectedCoverageAmount, "never")
        : undefined;
    const formattedCoveragePremium = selectedCoveragePremium ? formatUSCurrency(selectedCoveragePremium, "always") : undefined;

    return (
        <CardTemplate title={`Congratulations ${firstName}!`} className="text-base-content">
            <div className="font-medium text-base-content text-base flex flex-col gap-5">
                <p className="leading-6 text-base-content" id="approved-decision">
                    {isMLTA ? (
                        <>Your {formatRegisteredSymbol("Member Loyalty®")} Travel Accident Insurance is now active.</>
                    ) : isDTO ? (
                        <>
                            Congratulations! Your acceptance form has been processed and you are now the owner of {productName}.
                        </>
                    ) : (
                        <>Your Guaranteed Issue Whole Life Insurance is now active.</>
                    )}
                </p>

                <ul className="space-y-4 pl-4">
                    <li>
                        <div className="font-medium text-base-content text-sm">Policy Number:</div>
                        <div className="font-bold text-base-content text-lg">{policyNumber}</div>
                    </li>
                    <li>
                        <div className="font-medium text-base-content text-sm">Coverage {isMLTA ? "Plan" : "Amount"}:</div>
                        <div className="font-bold text-base-content text-lg">{formattedCoverageAmount}</div>
                    </li>
                    <li>
                        <div className="font-medium text-base-content text-sm">Monthly Payment:</div>
                        <div className="font-bold text-base-content text-lg">{formattedCoveragePremium}</div>
                    </li>
                </ul>

                <p className="font-medium text-sm leading-5 text-base-content">{confirmationText}</p>

                {isDTO && (
                    <CallToActionButton
                        applicantType={applicantType}
                        memberOfferAvailable={memberOfferAvailable}
                        spouseOfferAvailable={spouseOfferAvailable}
                        navigateTo={dtoNavigateTo}
                    />
                )}

                {isGIWL && (
                    <CallToActionButton
                        applicantType="member"
                        memberOfferAvailable={false}
                        spouseOfferAvailable={false}
                        href={giwlSpouseHref}
                    />
                )}
            </div>
            {/*<iframe src="https://aaalife.qualtrics.com/jfe/form/SV_e8saAErxsraQPrM"></iframe>*/}
        </CardTemplate>
    );
};

export default ApprovedDecision;
